/**
 * CartPage — the shopping cart.
 *
 * As requested, this page has NO top search navbar. It uses a minimal header
 * (back arrow + "My Cart") and is a standalone route (outside CustomerLayout).
 *
 * It reads the live cart from context: line items with steppers, a bill
 * summary (item total, savings, delivery, to pay), and a checkout button.
 * Checkout is UI-only (payments are out of MVP scope).
 */

import { useEffect, useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { ArrowLeft, ShoppingCart, Trash2, Clock, Tag } from 'lucide-react';
import { formatCurrency, computeDeliveryFee, productSizeLabel } from '@rocket07/shared';
import { useCart } from '@/context/CartContext';
import { useCollege } from '@/context/CollegeContext';
import { AddButton } from '@/components/product/AddButton';
import { Button, EmptyState } from '@/components/ui';
import { productImage } from '@/utils/productImage';
import { ROUTES } from '@/constants/routes';

export function CartPage() {
  const navigate = useNavigate();
  const { lines, itemCount, subtotal, savings, clear } = useCart();
  const { college } = useCollege();

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const deliveryFee = computeDeliveryFee(college, subtotal);
  const freeAbove = college?.freeDeliveryThreshold ?? 0;
  const toPay = subtotal + deliveryFee;

  return (
    <div className="min-h-dvh bg-canvas pb-28">
      {/* Minimal header — no search bar */}
      <header className="sticky top-0 z-40 flex items-center gap-3 bg-white px-4 py-3 shadow-nav">
        <button
          type="button"
          onClick={() => navigate(-1)}
          aria-label="Go back"
          className="grid h-10 w-10 place-items-center rounded-full border border-line text-ink hover:bg-canvas"
        >
          <ArrowLeft size={20} />
        </button>
        <h1 className="text-base font-bold text-ink">My Cart</h1>
        {itemCount > 0 && (
          <button
            type="button"
            onClick={clear}
            className="ml-auto inline-flex items-center gap-1 text-xs font-semibold text-danger"
          >
            <Trash2 size={14} /> Clear
          </button>
        )}
      </header>

      {itemCount === 0 ? (
        <EmptyState
          icon={ShoppingCart}
          title="Your cart is empty"
          description="Add items from the store and they will show up here."
          action={
            <Link to={ROUTES.home}>
              <Button>Browse products</Button>
            </Link>
          }
          className="min-h-[60vh]"
        />
      ) : (
        <div className="mx-auto max-w-2xl px-4">
          {/* Delivery estimate banner */}
          <div className="mt-3 flex items-center gap-2 rounded-xl bg-primary-50 px-4 py-3 text-sm text-primary-800">
            <Clock size={18} className="text-primary-600" />
            <span className="font-semibold">Delivery in 10–15 minutes</span>
          </div>

          {/* Line items */}
          <div className="mt-3 divide-y divide-line rounded-xl border border-line bg-white">
            {lines.map(({ product, quantity }) => (
              <div key={product.id} className="flex items-center gap-3 p-3">
                <Link
                  to={ROUTES.product(product.slug)}
                  className="grid h-16 w-16 shrink-0 place-items-center overflow-hidden rounded-lg bg-tile text-3xl"
                >
                  <LineThumb product={product} />
                </Link>
                <div className="min-w-0 flex-1">
                  <Link
                    to={ROUTES.product(product.slug)}
                    className="line-clamp-2 text-sm font-medium text-ink"
                  >
                    {product.name}
                  </Link>
                  <p className="text-xs text-muted">{productSizeLabel(product)}</p>
                  <p className="mt-0.5 text-sm font-bold text-ink">
                    {formatCurrency(product.price * quantity)}
                    {product.mrp > product.price && (
                      <span className="ml-1.5 text-xs font-normal text-muted line-through">
                        {formatCurrency(product.mrp * quantity)}
                      </span>
                    )}
                  </p>
                </div>
                <div className="w-24 shrink-0">
                  <AddButton product={product} />
                </div>
              </div>
            ))}
          </div>

          {/* Bill summary */}
          <div className="mt-4 rounded-xl border border-line bg-white p-4">
            <h2 className="text-sm font-bold text-ink">Bill Summary</h2>
            <dl className="mt-3 space-y-2 text-sm">
              <Row label={`Item total (${itemCount})`} value={formatCurrency(subtotal)} />
              {savings > 0 && (
                <Row
                  label="Product savings"
                  value={`− ${formatCurrency(savings)}`}
                  valueClass="text-success font-semibold"
                />
              )}
              <Row
                label="Delivery fee"
                value={deliveryFee === 0 ? 'FREE' : formatCurrency(deliveryFee)}
                valueClass={deliveryFee === 0 ? 'text-success font-semibold' : ''}
              />
              {deliveryFee > 0 && freeAbove > subtotal && (
                <p className="rounded-md bg-success/10 px-2.5 py-1.5 text-xs font-bold text-success">
                  Add {formatCurrency(freeAbove - subtotal)} more for free delivery
                </p>
              )}
              <div className="mt-2 flex items-center justify-between border-t border-line pt-3 text-base font-extrabold text-ink">
                <span>To Pay</span>
                <span>{formatCurrency(toPay)}</span>
              </div>
            </dl>
          </div>

          {savings > 0 && (
            <div className="mt-3 flex items-center gap-2 rounded-xl bg-success/10 px-4 py-3 text-sm font-semibold text-success">
              <Tag size={16} /> You save {formatCurrency(savings)} on this order
            </div>
          )}
        </div>
      )}

      {/* Sticky checkout bar */}
      {itemCount > 0 && (
        <div className="fixed inset-x-0 bottom-0 z-30 border-t border-line bg-white px-4 py-3 shadow-bar">
          <div className="mx-auto flex max-w-2xl items-center gap-3">
            <div className="leading-tight">
              <p className="text-lg font-extrabold text-ink">{formatCurrency(toPay)}</p>
              <p className="text-xs text-muted">{itemCount} items</p>
            </div>
            <Link to={ROUTES.checkout} className="flex-1">
              <Button fullWidth size="lg">
                Proceed to Checkout
              </Button>
            </Link>
          </div>
        </div>
      )}
    </div>
  );
}

function Row({ label, value, valueClass = '' }) {
  return (
    <div className="flex items-center justify-between">
      <dt className="text-muted">{label}</dt>
      <dd className={valueClass || 'text-ink'}>{value}</dd>
    </div>
  );
}

// Cart thumbnail: API image → name→photo map → emoji (if the file is missing).
function LineThumb({ product }) {
  const [failed, setFailed] = useState(false);
  const src = failed ? null : product.image || productImage(product.name);
  return src ? (
    <img
      src={src}
      alt={product.name}
      loading="lazy"
      onError={() => setFailed(true)}
      className="h-full w-full object-contain p-1"
    />
  ) : (
    <span aria-hidden="true">{product.emoji}</span>
  );
}

export default CartPage;
